import { createFileRoute, Link } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import {
  CheckList,
  ClosingCta,
  MarketingPage,
  MarketingSection,
  StartFreeButton,
} from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/study-planner")({
  head: () =>
    seo({
      title: "JEE Study Planner — Daily Plan, Backlogs & Streaks | The Quantum Mind",
      description:
        "Free JEE study planner that turns your syllabus, revisions, question targets and test tasks into one daily plan, with backlog carry-forward and streak tracking.",
      path: "/study-planner",
    }),
  component: StudyPlannerLanding,
});

function StudyPlannerLanding() {
  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        JEE study planner
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
        One daily plan for your whole preparation
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
        Instead of a timetable you abandon in week two, The Quantum Mind builds your plan from what
        you actually study. Revisions that are due, question targets, test preparation tasks and
        pending backlog all land in a single list for today.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <StartFreeButton label="Build my plan" />
        <Link
          to="/questions"
          className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
        >
          See question tracking
        </Link>
      </div>

      <MarketingSection title="What lands in your daily plan">
        <CheckList
          items={[
            "Revisions due today, across every chapter",
            "Question practice targets per chapter",
            "Test preparation tasks before each upcoming test",
            "Backlog carried forward from missed days",
            "Streak and daily completion percentage",
            "Subject-wise syllabus progress at a glance",
          ]}
        />
      </MarketingSection>

      <MarketingSection title="A simple weekly routine">
        <ol className="space-y-2 text-sm leading-relaxed text-muted-foreground">
          <li>1. Add the chapters you are currently studying, with lecture counts.</li>
          <li>2. Tick lectures the day you finish them.</li>
          <li>3. Clear today&apos;s revisions before starting new lectures.</li>
          <li>4. Log the questions you practised against the right chapter.</li>
          <li>5. Create a test plan as soon as a test date is announced.</li>
          <li>6. Clear backlog on the weekend.</li>
        </ol>
      </MarketingSection>

      <ClosingCta
        title="Start your JEE study plan today"
        body="Free account, no card required — your plan is saved and synced everywhere."
      />
    </MarketingPage>
  );
}
